import type { ReactNode } from 'react';
import { CopyIdButton } from '@/components/shared';
import { formatDateTime, formatInterviewLimit, formatShortId, formatStatus } from '../format';
import type {
  BackofficeDataSource,
  BackofficeProjectTargetScope,
  BackofficeTargetScopeContent,
  BackofficeTargetScopeField,
  BackofficeTargetScopeQuestion,
  BackofficeTargetScopeRecruit,
} from '../types';

export interface ProjectTargetScopePanelProps {
  targetScope: BackofficeProjectTargetScope;
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined || value === '') {
    return 'Not set';
  }
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return JSON.stringify(value, null, 2);
}

function formatCurrency(cents: number | null): string {
  if (cents === null) {
    return 'Not available';
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
}

function formatCountMap(counts: Record<string, number>): string {
  const entries = Object.entries(counts);
  if (entries.length === 0) {
    return 'None';
  }
  return entries.map(([status, count]) => `${formatStatus(status)} ${count}`).join(', ');
}

type TargetScopePanelSurface = 'page' | 'tile';

function Section({
  title,
  description,
  children,
  surface = 'page',
}: {
  title: string;
  description: string;
  children: ReactNode;
  surface?: TargetScopePanelSurface | undefined;
}) {
  return (
    <section
      className={
        surface === 'tile'
          ? 'space-y-5'
          : 'rounded-lg border border-neutral-200 bg-white p-5 shadow-sm'
      }
    >
      <div>
        <h2 className="font-inter-tight text-xl font-semibold text-neutral-900">{title}</h2>
        <p className="mt-1 text-small text-neutral-500">{description}</p>
      </div>
      <div className="mt-5 space-y-5">{children}</div>
    </section>
  );
}

function SourceUnavailable({
  source,
  status,
}: {
  source: string;
  status: BackofficeDataSource<unknown>;
}) {
  if (status.available) {
    return null;
  }

  return (
    <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-body text-red-700">
      {source} is unavailable: {status.error ?? 'Failed to load source data'}
    </div>
  );
}

function KeyValueGrid({ children }: { children: ReactNode }) {
  return <dl className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">{children}</dl>;
}

function KeyValue({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="rounded-md border border-neutral-100 bg-neutral-50 px-3 py-2">
      <dt className="text-small font-semibold text-neutral-900">{label}</dt>
      <dd className="mt-1 min-w-0 text-body text-neutral-700">{value}</dd>
    </div>
  );
}

function JsonDetails({ label, value }: { label: string; value: unknown }) {
  return (
    <details className="rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2">
      <summary className="cursor-pointer text-small font-semibold text-neutral-900">
        {label}
      </summary>
      <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap break-words text-xs text-neutral-700">
        {JSON.stringify(value ?? null, null, 2)}
      </pre>
    </details>
  );
}

function FieldBlock({ label, field }: { label: string; field: BackofficeTargetScopeField | null }) {
  return (
    <div className="rounded-lg border border-neutral-200">
      <div className="border-b border-neutral-100 px-4 py-3">
        <h3 className="text-body font-semibold text-neutral-900">{label}</h3>
      </div>
      {field && field.locales.length > 0 ? (
        <div className="divide-y divide-neutral-100">
          {field.locales.map((locale) => (
            <div
              key={`${field.fieldKey}-${locale.locale}`}
              className="grid gap-2 px-4 py-3 lg:grid-cols-[7rem_minmax(0,1fr)_12rem]"
            >
              <div className="text-small font-semibold text-neutral-600">{locale.locale}</div>
              <div className="min-w-0 whitespace-pre-wrap break-words text-body text-neutral-800">
                {formatValue(locale.value)}
              </div>
              <div className="text-small text-neutral-500">
                Rev {locale.revision} · {formatDateTime(locale.updatedAt)}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="px-4 py-3 text-body text-neutral-500">Not set</div>
      )}
    </div>
  );
}

function QuestionList({ questions }: { questions: BackofficeTargetScopeQuestion[] }) {
  if (questions.length === 0) {
    return (
      <div className="rounded-lg border border-neutral-200 px-4 py-8 text-center text-body text-neutral-500">
        No questions configured
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {questions.map((question, index) => (
        <div key={question.questionId} className="rounded-lg border border-neutral-200">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-neutral-100 px-4 py-3">
            <div>
              <h3 className="text-body font-semibold text-neutral-900">
                Question {index + 1} · {formatStatus(question.questionType)}
              </h3>
              <p className="text-small text-neutral-500">
                {question.required ? 'Required' : 'Optional'} · {question.followUpCount} follow-up
                {question.followUpCount === 1 ? '' : 's'} · Core rev {question.coreRevision}
              </p>
            </div>
            <div className="inline-flex items-center gap-2 text-small text-neutral-500">
              {formatShortId(question.questionId)}
              <CopyIdButton value={question.questionId} label="Copy question ID" />
            </div>
          </div>
          <div className="divide-y divide-neutral-100">
            {question.locales.map((locale) => (
              <div
                key={`${question.questionId}-${locale.locale}`}
                className="grid gap-2 px-4 py-3 lg:grid-cols-[7rem_minmax(0,1fr)_12rem]"
              >
                <div className="text-small font-semibold text-neutral-600">{locale.locale}</div>
                <div className="min-w-0 text-body text-neutral-800">
                  <p className="whitespace-pre-wrap break-words">{locale.text}</p>
                  {locale.options.length > 0 ? (
                    <p className="mt-1 text-small text-neutral-500">
                      Options: {locale.options.join(', ')}
                    </p>
                  ) : null}
                  {locale.followUpGuidance ? (
                    <p className="mt-1 text-small text-neutral-500">
                      Guidance: {locale.followUpGuidance}
                    </p>
                  ) : null}
                </div>
                <div className="text-small text-neutral-500">
                  Rev {locale.revision} · {formatDateTime(locale.updatedAt)}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function CreateSection({
  content,
  surface,
}: {
  content: BackofficeTargetScopeContent;
  surface?: TargetScopePanelSurface | undefined;
}) {
  return (
    <Section
      title="Create"
      description="Research objective, target audience, interview goal and drafted questions."
      surface={surface}
    >
      <KeyValueGrid>
        <KeyValue label="Default locale" value={content.defaultLocale ?? 'Not set'} />
        <KeyValue
          label="Available locales"
          value={content.availableLocales.length > 0 ? content.availableLocales.join(', ') : 'None'}
        />
        <KeyValue
          label="Interview target"
          value={formatInterviewLimit(content.create.interviewTarget)}
        />
        <KeyValue label="Project version" value={content.projectVersion ?? 'Not available'} />
        <KeyValue label="Structure revision" value={content.structureRevision ?? 'Not available'} />
      </KeyValueGrid>
      <FieldBlock label="Objective" field={content.create.objective} />
      <FieldBlock label="Target group" field={content.create.targetGroup} />
      <QuestionList questions={content.create.questions} />
    </Section>
  );
}

function DesignSection({
  content,
  surface,
}: {
  content: BackofficeTargetScopeContent;
  surface?: TargetScopePanelSurface | undefined;
}) {
  const settings = content.design.settings;
  const brand = content.design.brand;

  return (
    <Section
      title="Design"
      description="Participant-facing copy, interview experience settings, moderators and branding."
      surface={surface}
    >
      <KeyValueGrid>
        <KeyValue
          label="Moderators"
          value={settings?.moderatorIds.length ? settings.moderatorIds.join(', ') : 'None'}
        />
        <KeyValue
          label="Assistant speed"
          value={settings ? String(settings.assistantTalkingSpeed) : 'Not available'}
        />
        <KeyValue
          label="Preview completed"
          value={settings ? (settings.previewCompleted ? 'Yes' : 'No') : 'Not available'}
        />
        <KeyValue
          label="Participant organization"
          value={settings?.organisationName ?? 'Not set'}
        />
        <KeyValue label="Brand selection" value={brand.binding?.selectionMode ?? 'Not available'} />
        <KeyValue
          label="Resolved brand"
          value={brand.resolvedBrand?.locale?.brandName ?? brand.resolvedBrand?.id ?? 'None'}
        />
      </KeyValueGrid>
      <FieldBlock label="Participant heading" field={content.design.participantHeading} />
      <FieldBlock label="Participant title" field={content.design.participantTitle} />
      <FieldBlock label="Participant description" field={content.design.participantDescription} />
      <FieldBlock label="Opening message" field={content.design.opening} />
      <FieldBlock label="Closing message" field={content.design.closing} />
    </Section>
  );
}

function RecruitSection({
  recruit,
  surface,
}: {
  recruit: BackofficeTargetScopeRecruit;
  surface?: TargetScopePanelSurface | undefined;
}) {
  return (
    <Section
      title="Recruit"
      description="Panel recruitment and own-participant setup without personal assignment links or tokens."
      surface={surface}
    >
      <KeyValueGrid>
        <KeyValue label="Panel drafts" value={String(recruit.panels.drafts.length)} />
        <KeyValue label="Panel orders" value={String(recruit.panels.orders.length)} />
        <KeyValue label="Participant imports" value={String(recruit.participants.imports.length)} />
        <KeyValue
          label="Participant segments"
          value={String(recruit.participants.segments.length)}
        />
        <KeyValue
          label="Participant assignments"
          value={String(recruit.participants.assignmentTotal)}
        />
        <KeyValue
          label="Assignment statuses"
          value={formatCountMap(recruit.participants.assignmentStatusCounts)}
        />
      </KeyValueGrid>

      <div className="space-y-3">
        <h3 className="text-body font-semibold text-neutral-900">Panel drafts</h3>
        {recruit.panels.drafts.length > 0 ? (
          recruit.panels.drafts.map((draft) => (
            <div key={draft.id} className="rounded-lg border border-neutral-200 px-4 py-3">
              <KeyValueGrid>
                <KeyValue label="Provider" value={draft.provider} />
                <KeyValue label="Status" value={formatStatus(draft.status)} />
                <KeyValue label="Places" value={String(draft.totalPlaces)} />
                <KeyValue label="Estimated duration" value={`${draft.estimatedMinutes} min`} />
                <KeyValue label="Total cost" value={formatCurrency(draft.totalCostCents)} />
                <KeyValue label="Updated" value={formatDateTime(draft.updatedAt)} />
              </KeyValueGrid>
              <div className="mt-3 grid gap-3 lg:grid-cols-2">
                <JsonDetails label="Audience filters" value={draft.filters} />
                <JsonDetails label="Provider studies" value={draft.studies} />
              </div>
            </div>
          ))
        ) : (
          <p className="text-body text-neutral-500">No panel drafts</p>
        )}
      </div>

      <div className="space-y-3">
        <h3 className="text-body font-semibold text-neutral-900">Panel orders</h3>
        {recruit.panels.orders.length > 0 ? (
          recruit.panels.orders.map((order) => (
            <div key={order.id} className="rounded-lg border border-neutral-200 px-4 py-3">
              <KeyValueGrid>
                <KeyValue label="Provider" value={order.provider} />
                <KeyValue label="Status" value={formatStatus(order.status)} />
                <KeyValue label="Language" value={order.language} />
                <KeyValue label="Places" value={`${order.filledPlaces} / ${order.totalPlaces}`} />
                <KeyValue label="Approved" value={String(order.approvedCount)} />
                <KeyValue label="Rejected" value={String(order.rejectedCount)} />
                <KeyValue label="Returned" value={String(order.returnedCount)} />
                <KeyValue label="Total cost" value={formatCurrency(order.totalCostCents)} />
                <KeyValue
                  label="Payments"
                  value={`${order.paymentCount} (${order.paymentPlacesAdded} places)`}
                />
                <KeyValue label="Submissions" value={String(order.submissionCount)} />
                <KeyValue
                  label="Submission statuses"
                  value={formatCountMap(order.submissionStatusCounts)}
                />
                <KeyValue label="Launched" value={formatDateTime(order.launchedAt)} />
              </KeyValueGrid>
              <div className="mt-3">
                <JsonDetails label="Audience filters" value={order.filters} />
              </div>
            </div>
          ))
        ) : (
          <p className="text-body text-neutral-500">No panel orders</p>
        )}
      </div>

      <div className="space-y-3">
        <h3 className="text-body font-semibold text-neutral-900">Own participant imports</h3>
        {recruit.participants.imports.length > 0 ? (
          <div className="overflow-x-auto rounded-lg border border-neutral-200">
            <table className="min-w-full divide-y divide-neutral-200 text-left text-body">
              <thead className="bg-neutral-50 text-small font-semibold uppercase tracking-wide text-neutral-500">
                <tr>
                  <th className="px-4 py-3">File</th>
                  <th className="px-4 py-3">Rows</th>
                  <th className="px-4 py-3">Dedupe</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100 bg-white">
                {recruit.participants.imports.map((item) => (
                  <tr key={item.id}>
                    <td className="px-4 py-3 font-medium text-neutral-900">{item.fileName}</td>
                    <td className="px-4 py-3 text-neutral-700">
                      {item.rowCount} ({item.createdCount} created, {item.mergedCount} merged)
                    </td>
                    <td className="px-4 py-3 text-neutral-700">{item.dedupeField}</td>
                    <td className="px-4 py-3 text-neutral-700">{formatStatus(item.status)}</td>
                    <td className="px-4 py-3 text-neutral-700">{formatDateTime(item.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-body text-neutral-500">No participant imports</p>
        )}
      </div>

      <div className="space-y-3">
        <h3 className="text-body font-semibold text-neutral-900">Participant segments</h3>
        {recruit.participants.segments.length > 0 ? (
          recruit.participants.segments.map((segment) => (
            <div key={segment.id} className="rounded-lg border border-neutral-200 px-4 py-3">
              <KeyValueGrid>
                <KeyValue label="Name" value={segment.name} />
                <KeyValue label="Source" value={formatStatus(segment.source)} />
                <KeyValue label="Status" value={formatStatus(segment.status)} />
                <KeyValue label="Assignments" value={String(segment.assignmentCount)} />
                <KeyValue label="Updated" value={formatDateTime(segment.updatedAt)} />
              </KeyValueGrid>
              <div className="mt-3 grid gap-3 lg:grid-cols-2">
                <JsonDetails label="Segment filters" value={segment.filters} />
                <JsonDetails label="Quota" value={segment.quota} />
              </div>
            </div>
          ))
        ) : (
          <p className="text-body text-neutral-500">No participant segments</p>
        )}
      </div>
    </Section>
  );
}

export function ProjectTargetScopeSummaryPanel({ targetScope }: ProjectTargetScopePanelProps) {
  return (
    <KeyValueGrid>
      <KeyValue label="Study" value={targetScope.project.title} />
      <KeyValue label="Organization" value={targetScope.project.organizationName} />
      <KeyValue label="Status" value={formatStatus(targetScope.project.status)} />
      <KeyValue
        label="Interview limit"
        value={formatInterviewLimit(targetScope.project.interviewLimit)}
      />
      <KeyValue label="Interviews" value={String(targetScope.project.interviewCount)} />
      <KeyValue
        label="Completed interviews"
        value={String(targetScope.project.completedInterviewCount)}
      />
    </KeyValueGrid>
  );
}

export function ProjectTargetScopeCreatePanel({ targetScope }: ProjectTargetScopePanelProps) {
  if (!targetScope.content.available) {
    return <SourceUnavailable source="Study content" status={targetScope.content} />;
  }

  if (!targetScope.content.data) {
    return <p className="text-body text-neutral-500">No study content available</p>;
  }

  return <CreateSection content={targetScope.content.data} surface="tile" />;
}

export function ProjectTargetScopeDesignPanel({ targetScope }: ProjectTargetScopePanelProps) {
  if (!targetScope.content.available) {
    return <SourceUnavailable source="Study content" status={targetScope.content} />;
  }

  if (!targetScope.content.data) {
    return <p className="text-body text-neutral-500">No study content available</p>;
  }

  return <DesignSection content={targetScope.content.data} surface="tile" />;
}

export function ProjectTargetScopeRecruitPanel({ targetScope }: ProjectTargetScopePanelProps) {
  if (!targetScope.recruit.available) {
    return <SourceUnavailable source="Recruitment data" status={targetScope.recruit} />;
  }

  if (!targetScope.recruit.data) {
    return <p className="text-body text-neutral-500">No recruitment data available</p>;
  }

  return <RecruitSection recruit={targetScope.recruit.data} surface="tile" />;
}

export function ProjectTargetScopePanel({ targetScope }: ProjectTargetScopePanelProps) {
  return (
    <div className="space-y-5">
      <div className="rounded-lg border border-neutral-200 bg-white p-5 shadow-sm">
        <ProjectTargetScopeSummaryPanel targetScope={targetScope} />
      </div>

      <SourceUnavailable source="Study content" status={targetScope.content} />
      {targetScope.content.available && targetScope.content.data ? (
        <>
          <CreateSection content={targetScope.content.data} />
          <DesignSection content={targetScope.content.data} />
        </>
      ) : null}

      <SourceUnavailable source="Recruitment data" status={targetScope.recruit} />
      {targetScope.recruit.available && targetScope.recruit.data ? (
        <RecruitSection recruit={targetScope.recruit.data} />
      ) : null}
    </div>
  );
}
